import React, { useState, useEffect } from 'react';
import { debounce } from 'throttle-debounce';
import PropTypes from 'prop-types';
import Icon from '@welldev-group/rcl-icon';
import Input from '@welldev-group/rcl-input';
import Button from '@welldev-group/rcl-button';
import SelectBox from '@welldev-group/rcl-select-box';
import { CheckBox, I18nText } from '@welldev-group/react-comp-lib';
import Modal from '@welldev-group/rcl-modal';
import TextEditor from '../TextEditor';

import httpClient from '../../../shared/libraries/httpClient';
import routes from '../../../shared/routes';
import selectBoxObjectMapper from '../../../shared/helper/selectBoxObjectMapper';

const SupportTicketModal = props => {
  const { fetchTickets, signedInAsSuperAdmin } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [affiliates, setAffiliates] = useState([]);
  const [apps, setApps] = useState([]);
  const [users, setUsers] = useState([]);
  const [affiliateId, setAffiliateId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [onBehalfOfUser, setOnBehalfOfUser] = useState(false);
  const [app, setApp] = useState(null);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [attachmentIds, setAttachmentIds] = useState([]);

  const initializeSupportTicket = async () => {
    const { data } = await httpClient.get(routes.support.tickets.new());
    setApps(data.apps);
  };

  const createAndFetchTicket = async () => {
    const createTicketParams = {
      affiliate_id: affiliateId,
      user_id: userId,
      app,
      on_behalf_of_user: onBehalfOfUser ? '1' : '0',
      subject: ticketSubject,
      opening_message_attributes: { body: ticketDescription },
      attachment_ids: attachmentIds,
    };

    const { data } = await httpClient.post(routes.support.tickets.create(), {
      support_ticket: createTicketParams,
    });

    if (data.error === null) {
      fetchTickets();
      setModalOpen(false);
    }
  };

  const isBlankDescription = () => {
    return !ticketDescription.replace(/<(?!img)[^>]+>|[\s]+/g, '');
  };

  const disableSubmitButton = () => {
    if (signedInAsSuperAdmin) {
      return (
        !affiliateId ||
        !userId ||
        !app ||
        !ticketSubject ||
        isBlankDescription()
      );
    }
    return !app || !ticketSubject || isBlankDescription();
  };

  useEffect(() => {
    if (!apps.length) {
      initializeSupportTicket();
    }
  });

  const fetchAffiliates = debounce(2000, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.affiliates.list({ q: inputValue }),
      );
      setAffiliates(
        data.affiliates.map(affiliate => ({
          value: affiliate.id,
          label: affiliate.clientName,
        })),
      );
    }
  });

  const fetchUsers = debounce(2000, async inputValue => {
    if (inputValue.length > 1) {
      const { data } = await httpClient.get(
        routes.users.list({ q: inputValue }),
      );

      const userOptions = data.users.map(user => ({
        label: user.name,
        value: user.id,
      }));
      setUsers(userOptions);
    }
  });

  const onAffiliateIdChange = selectedOption =>
    setAffiliateId(selectedOption && selectedOption.value);
  const onUserIdChange = selectedOption =>
    setUserId(selectedOption && selectedOption.value);
  const toggleOnBehalfOfUser = event => setOnBehalfOfUser(event.target.checked);
  const onAppChange = selectedOption =>
    setApp(selectedOption && selectedOption.value);
  const onTicketSubjectChange = value => setTicketSubject(value);

  const onTicketDescriptionChange = text => {
    setTicketDescription(text);
  };

  const handleAttachmentId = id => {
    setAttachmentIds(ids => [...ids, id]);
  };

  return (
    <>
      <Button
        className="button button--v2 button--small button--primary modal-toggle"
        version="v2"
        size="small"
        fullWidth={false}
        type="primary"
        label={<I18nText id="support.create" />}
        onClick={() => {
          setModalOpen(true);
        }}
      />
      <Modal
        className="modal__wrapper modal__wrapper-content"
        isVisible={modalOpen}
        onOutsideClick={() => setModalOpen(false)}
      >
        <>
          <div className="modal__header col-12 col-bleed-y">
            <Modal.Title>
              <I18nText id="support.ticket.create" />
            </Modal.Title>
            <div
              onClick={() => setModalOpen(false)}
              onKeyDown={() => setModalOpen(false)}
              role="presentation"
            >
              <Icon name="close" size="normal" />
            </div>
          </div>
          <Modal.Content>
            <div className="grid">
              <div className="support-ticket-modal col-12">
                {signedInAsSuperAdmin && (
                  <div className="support-ticket-modal__created-for-checkbox">
                    <CheckBox
                      label={
                        <I18nText id="support.ticket.created_for_client" />
                      }
                      name="app-list"
                      onChange={toggleOnBehalfOfUser}
                      size="small"
                      checked={onBehalfOfUser}
                    />
                  </div>
                )}

                {signedInAsSuperAdmin && onBehalfOfUser && (
                  <div className="support-ticket-modal__affiliates-list">
                    <SelectBox
                      label={I18n.t(
                        'components.support.ticket.placeholder.client',
                      )}
                      onChange={onAffiliateIdChange}
                      onInputChange={fetchAffiliates}
                      options={affiliates}
                      placeholderText={
                        <I18nText id="support.ticket.placeholder.select" />
                      }
                      width="full"
                      required={true}
                      value={selectBoxObjectMapper(affiliates, affiliateId)}
                    />
                  </div>
                )}

                {signedInAsSuperAdmin && onBehalfOfUser && (
                  <div className="support-ticket-modal__users-list">
                    <SelectBox
                      label={I18n.t(
                        'components.support.ticket.placeholder.user',
                      )}
                      onChange={onUserIdChange}
                      onInputChange={fetchUsers}
                      options={users}
                      placeholderText={
                        <I18nText id="support.ticket.placeholder.select" />
                      }
                      width="full"
                      value={selectBoxObjectMapper(users, userId)}
                    />
                  </div>
                )}

                <div className="support-ticket-modal__apps-list">
                  <SelectBox
                    label={I18n.t(
                      'components.support.ticket.placeholder.application',
                    )}
                    onChange={onAppChange}
                    options={apps}
                    placeholderText={
                      <I18nText id="support.ticket.placeholder.select" />
                    }
                    width="full"
                    value={selectBoxObjectMapper(apps, app)}
                  />
                </div>

                <div className="support-ticket-modal__ticket-subject">
                  <Input
                    label={I18n.t(
                      'components.support.ticket.placeholder.subject',
                    )}
                    placeholder={I18n.t(
                      'components.support.ticket.placeholder.subject',
                    )}
                    value={ticketSubject}
                    onChange={onTicketSubjectChange}
                  />
                </div>

                <div className="support-ticket-modal__ticket-description-label">
                  {I18n.t(
                    'components.support.ticket.placeholder.issue_with_label',
                  )}
                </div>
                <div className="support-ticket-modal__ticket-description">
                  <TextEditor
                    placeholder={I18n.t(
                      'components.support.ticket.placeholder.issue',
                    )}
                    onAttachmentUpload={handleAttachmentId}
                    onChange={onTicketDescriptionChange}
                    value={ticketDescription}
                  />
                </div>
              </div>
            </div>
            <div className="modal__wrapper-footer-left col-12 col-bleed-y">
              <div className="support-ticket-modal__bottom">
                <Button
                  onClick={createAndFetchTicket}
                  label={<I18nText id="support.create" />}
                  version="v2"
                  type="primary"
                  size="small"
                  disabled={disableSubmitButton()}
                />
                <Button
                  onClick={() => setModalOpen(false)}
                  label={<I18nText id="support.ticket.cancel" />}
                  version="v2"
                  size="small"
                />
              </div>
            </div>
          </Modal.Content>
        </>
      </Modal>
    </>
  );
};

SupportTicketModal.propTypes = {
  fetchTickets: PropTypes.func.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
};

export default SupportTicketModal;
